import {
    GOVERNMENT_OFFER_PRIORITY,
    INSURANCE_OFFER_PRIORITY,
    isGovernmentOfferType,
    isInsuranceOfferType,
    isSelfPayOfferType,
    SELF_PAY_OFFER_PRIORITY,
} from '@phx/data-sources/medication-marketplace';

import type { PathwayOptionConfig } from '../configuration/shared';
import { useMedicationConfigOverride } from '../contexts/DrugProgramProvider';
import type { FillPathway } from '../contexts/fill';
import { useFillPrescription } from '../contexts/fill/use-fill-prescription';
import type {
    HeadlinePrice,
    MedicationConfigOverride,
} from '../hooks/use-config-api';
import { getBestOffer } from '../hooks/use-prescription-offers';
import { useProgramTerms } from '../hooks/use-program-terms';
import { getMedicationNdc } from '../util/get-medication-ndc';

const resolveTargetNdc = (
    fillPathway: FillPathway,
    currentNdc: string | undefined,
    medConfig: MedicationConfigOverride
): string | null => {
    if (!currentNdc) return null;
    const v2Configs = medConfig.ndcSwitchConfigsV2?.[currentNdc];
    const v2Target = v2Configs
        ? (
              v2Configs as Record<
                  string,
                  (typeof v2Configs)[keyof typeof v2Configs]
              >
          )[fillPathway]?.[0]?.targets[0]?.ndc
        : undefined;
    return v2Target ?? null;
};

// Bridge card: main price = switch target's MedicareBridge price.
// Falls back to current NDC's MedicareBridge when no switch target (e.g. KwikPen IS the target).
const resolveGovBridgePrice = (
    fillPathway: FillPathway,
    currentNdc: string | undefined,
    medConfig: MedicationConfigOverride
): number | null => {
    if (fillPathway !== 'government') return null;
    const targetNdc =
        resolveTargetNdc(fillPathway, currentNdc, medConfig) ?? currentNdc;
    if (!targetNdc) return null;
    return (
        medConfig.ndcInfo[targetNdc]?.headlinePrices?.find(
            (p) => p.type === 'MedicareBridge'
        )?.value ?? null
    );
};

// Gray subcard fallback price — logic differs by card type:
// Insurance: NonCoveredBenefit or Cash (the non-covered scenario price)
// Government/bridge: Incentive or Cash (self-pay fallback if bridge not approved)
//   Falls back to switch target's price when prescribed drug has none (e.g. AIJ)
const resolveFallbackPrice = (
    fillPathway: FillPathway,
    fallbackPriceLabel: string | undefined,
    currentNdc: string | undefined,
    currentNdcPrices: HeadlinePrice[],
    medConfig: MedicationConfigOverride
): number | null => {
    if (!fallbackPriceLabel) return null;

    if (fillPathway === 'insurance') {
        return (
            (currentNdc
                ? medConfig.staticPricing?.nonCoveredBenefit?.[currentNdc]
                : null) ??
            currentNdcPrices.find((p) => p.type === 'NonCoveredBenefit')
                ?.value ??
            currentNdcPrices.find((p) => p.type === 'Cash')?.value ??
            null
        );
    }

    const directPrice =
        currentNdcPrices.find((p) => p.type === 'Incentive')?.value ??
        currentNdcPrices.find((p) => p.type === 'Cash')?.value;

    if (directPrice != null) return directPrice;

    const targetNdc = resolveTargetNdc(fillPathway, currentNdc, medConfig);
    if (!targetNdc) return null;
    const targetPrices = medConfig.ndcInfo[targetNdc]?.headlinePrices ?? [];
    return (
        targetPrices.find((p) => p.type === 'Incentive')?.value ??
        targetPrices.find((p) => p.type === 'Cash')?.value ??
        null
    );
};

// Self-pay card: main price = Incentive or CashAssistance, falling back to Cash.
// Checks current NDC first, then switch target when current NDC has none.
// AIJ has no self-pay switch target — falls back to the government (bridge) target instead.
const resolveSelfPayPrice = (
    fillPathway: FillPathway,
    currentNdc: string | undefined,
    currentNdcPrices: HeadlinePrice[],
    medConfig: MedicationConfigOverride
): number | null => {
    if (fillPathway !== 'self-pay') return null;

    const pickPrice = (prices: HeadlinePrice[]) =>
        prices.find((p) => p.type === 'Incentive')?.value ??
        prices.find((p) => p.type === 'CashAssistance')?.value ??
        prices.find((p) => p.type === 'Cash')?.value;

    const directPrice = pickPrice(currentNdcPrices);
    if (directPrice != null) return directPrice;

    const targetNdc = resolveTargetNdc('self-pay', currentNdc, medConfig);
    if (!targetNdc) return null;
    const targetPrices = medConfig.ndcInfo[targetNdc]?.headlinePrices ?? [];
    return pickPrice(targetPrices) ?? null;
};

export const useCardPricing = (option: PathwayOptionConfig) => {
    const { allOffers, medication } = useFillPrescription();
    const medConfig = useMedicationConfigOverride();
    const selfPayTerms = useProgramTerms(medication, 'self-pay');

    const currentNdc = getMedicationNdc(medication);
    const currentNdcPrices = currentNdc
        ? (medConfig.ndcInfo[currentNdc]?.headlinePrices ?? [])
        : [];

    const switchTargetGovPrice = resolveGovBridgePrice(
        option.fillPathway,
        currentNdc,
        medConfig
    );

    const fallbackPrice = resolveFallbackPrice(
        option.fillPathway,
        option.content.fallbackPriceLabel,
        currentNdc,
        currentNdcPrices,
        medConfig
    );

    // Insurance card: main price = staticPricing.insurance config value (e.g. $25 copay).
    const insuranceStaticPrice =
        option.fillPathway === 'insurance' && currentNdc
            ? (medConfig.staticPricing?.insurance?.[currentNdc] ?? null)
            : null;

    const selfPayPrice = resolveSelfPayPrice(
        option.fillPathway,
        currentNdc,
        currentNdcPrices,
        medConfig
    );

    const offers = allOffers.filter(
        option.fillPathway === 'insurance'
            ? (o) => isInsuranceOfferType(o.headlinePriceType)
            : option.fillPathway === 'government'
              ? (o) => isGovernmentOfferType(o.headlinePriceType)
              : (o) => isSelfPayOfferType(o.headlinePriceType)
    );
    const offer = getBestOffer(
        offers,
        option.fillPathway === 'insurance'
            ? INSURANCE_OFFER_PRIORITY
            : option.fillPathway === 'government'
              ? GOVERNMENT_OFFER_PRIORITY
              : SELF_PAY_OFFER_PRIORITY
    );

    const termsUrl =
        option.fillPathway === 'self-pay'
            ? (selfPayTerms?.termsUrl ?? null)
            : null;

    return {
        displayPrice:
            switchTargetGovPrice ?? insuranceStaticPrice ?? selfPayPrice,
        fallbackPrice,
        offer,
        fallbackDisclaimer: option.content.fallbackDisclaimer ?? null,
        disclaimer: option.content.disclaimer ?? null,
        termsUrl,
    };
};
